import { useEffect, useState } from "react";
import { http } from "../../axios/axios";
import { useNavigate, useParams } from "react-router-dom";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import {
    BadgeCheck,
    Building2,
    CheckCircle2,
    Clock3,
    MapPin,
} from "lucide-react";
import { Helmet } from "react-helmet-async";

const ProjectInfoCard = ({
    icon: Icon,
    label,
    value,
    iconColor = "text-blue-600",
    valueColor = "text-gray-900",
}) => {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <Icon className={`mb-4 h-8 w-8 ${iconColor}`} />
            <dt className="text-sm font-medium uppercase tracking-wide text-gray-500">
                {label}
            </dt>
            <dd className={`mt-2 text-lg font-semibold capitalize ${valueColor}`}>
                {value}
            </dd>
        </div>
    );
};

const ProjectContentSection = ({ id, title, subtitle, content }) => (
    <section
        aria-labelledby={id}
        className="mt-12 md:mt-16 border-t border-gray-200 pt-10 md:pt-14"
    >
        <header className="mb-6 md:mb-8 ">
            <h2
                id={id}
                className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-gray-900 leading-tight"
            >
                {title}
            </h2>

            <p className="mt-3 text-sm sm:text-base leading-7 text-gray-600">
                {subtitle}
            </p>
        </header>

        <div className="">
            <p className="text-[15px] sm:text-base lg:text-lg leading-7 lg:leading-8 text-gray-700 whitespace-pre-line text-justify">
                {content}
            </p>
        </div>
    </section>
);

export default function ProjectDetails() {
    const { slug } = useParams();
    const [projectDetail, setProjectDetail] = useState([]);
    const navigate = useNavigate();

    const fetchProject = async () => {
        try {
            const projectRes = await http.get(`projects/${slug}`);

            setProjectDetail(projectRes.data.project);
        } catch (error) {
            console.log(error);
        }
    };

    const getYoutubeId = (url) => {
        if (!url) return null;
        const regExp = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/;
        const match = url.match(regExp);
        return match ? match[1] : null;
    };
    const seo = projectDetail?.seo;

    let schema = null;

    try {
        if (seo?.schemaScript) {
            schema =
                typeof seo.schemaScript === "string"
                    ? JSON.parse(seo.schemaScript)
                    : seo.schemaScript;
        }
    } catch (err) {
        console.error("Invalid schema JSON", err);
    }
    useEffect(() => {
        if (slug) {
            fetchProject();
        }
    }, [slug]);

    return (
        <>
            <Helmet>
                <title>{seo?.metaTitle || projectDetail?.title}</title>

                <meta
                    name="description"
                    content={seo?.metaDescription || projectDetail?.shortDescription}
                />

                <meta name="keywords" content={seo?.metaTags?.join(", ") || ""} />

                {schema && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(schema),
                        }}
                    />
                )}
            </Helmet>
            <main>
                <section
                    className="
relative
overflow-hidden
min-h-[280px]
h-[40vh]
md:h-[50vh]
lg:h-[60vh]
"
                >
                    <figure className="absolute inset-0">
                        <img
                            src={GetImageUrl(projectDetail?.bannerImage)}
                            alt={projectDetail?.bannerAlt}
                            className="
              w-full
              h-full
              object-cover
            "
                        />

                        <figcaption className="sr-only">
                            {projectDetail?.bannerAlt}
                        </figcaption>
                    </figure>

                    <div className="absolute inset-0 bg-black/60" />

                    <header
                        className="
            relative
            z-10
            h-full
            flex
            flex-col
            justify-center
            items-center
            text-center
            px-5
            sm:px-8
            md:px-16
            lg:px-24
          "
                    >
                        <h1
                            className="
              text-white
              text-2xl
            font-serif
              md:text-3xl
              xl:text-4xl
              2xl:text-5xl
              font-bold
              leading-tight
              max-w-7xl
            "
                        >
                            {projectDetail?.title}
                        </h1>
                        <p className="hidden md:block mt-4 text-lg text-gray-200 max-w-3xl">
                            {projectDetail.shortDescription}
                        </p>
                    </header>
                </section>
                <article
                    className="

    px-4
    sm:px-6
    lg:px-8
    xl:px-10
    py-10
    md:py-14
    lg:py-16
  "
                >
                    {/** ProjectInfoCard */}
                    <section aria-labelledby="project-information">
                        <dl className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                            {projectDetail.clientName && (
                                <ProjectInfoCard
                                    icon={Building2}
                                    label="Client"
                                    value={projectDetail.clientName}
                                    iconColor="text-blue-600"
                                />
                            )}
                            {projectDetail.location && (
                                <ProjectInfoCard
                                    icon={MapPin}
                                    label="Location"
                                    value={projectDetail.location}
                                    iconColor="text-red-500"
                                />
                            )}
                            {projectDetail.projectDuration && (
                                <ProjectInfoCard
                                    icon={Clock3}
                                    label="Duration"
                                    value={projectDetail.projectDuration}
                                    iconColor="text-amber-500"
                                />
                            )}
                            {projectDetail.status && (
                                <ProjectInfoCard
                                    icon={BadgeCheck}
                                    label="Status"
                                    value={projectDetail.status}
                                    iconColor={
                                        projectDetail.status === "completed"
                                            ? "text-green-600"
                                            : projectDetail.status === "ongoing"
                                                ? "text-blue-600"
                                                : "text-amber-500"
                                    }
                                    valueColor={
                                        projectDetail.status === "completed"
                                            ? "text-green-600"
                                            : projectDetail.status === "ongoing"
                                                ? "text-blue-600"
                                                : "text-amber-500"
                                    }
                                />
                            )}
                        </dl>
                    </section>

                    {projectDetail?.description && (
                        <ProjectContentSection
                            id="project-overview"
                            title="Project Overview"
                            subtitle="Learn about the scope, objectives, and execution of this security system installation project."
                            content={projectDetail.description}
                        />
                    )}

                    {projectDetail?.services?.length > 0 && (
                        <section
                            aria-labelledby="services-delivered"
                            className="mt-10 border-t border-gray-200 pt-14"
                        >
                            <header className="mb-8">
                                <h2
                                    className="text-2xl md:text-3xl font-bold font-serif text-gray-900"
                                    id="services-delivered"
                                >
                                    Services Delivered
                                </h2>
                                <p className="mt-2 text-gray-600 leading-7">
                                    This project included the following security solutions and
                                    professional installation services tailored to the client's
                                    requirements.
                                </p>
                            </header>
                            <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                {projectDetail.services?.map((service) => (
                                    <li
                                        key={service}
                                        className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-600 hover:shadow-lg"
                                    >
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                                            <CheckCircle2 size={22} />
                                        </div>

                                        <span className="text-base font-semibold text-gray-800">
                                            {service}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {(projectDetail?.brands?.length > 0 ||
                        projectDetail?.products?.length > 0) && (
                            <section
                                aria-labelledby="brands-and-products"
                                className="mt-10 border-t border-gray-200 pt-10 md:pt-14"
                            >
                                <header className="mb-8 md:mb-10">
                                    <h2
                                        id="brands-and-products"
                                        className="text-2xl md:text-3xl font-bold font-serif text-gray-900"
                                    >
                                        Brands & Products Used
                                    </h2>

                                    <p className="mt-3 text-gray-600 leading-7">
                                        This security project was executed using trusted surveillance
                                        brands and enterprise-grade security products to ensure
                                        long-term performance, reliability, and scalability.
                                    </p>
                                </header>

                                <div className="space-y-12">
                                    {/* Brands */}
                                    {projectDetail?.brands?.length > 0 && (
                                        <section aria-labelledby="project-brands">
                                            <h3
                                                id="project-brands"
                                                className="mb-6 text-xl font-semibold text-gray-900"
                                            >
                                                Brands Used
                                            </h3>

                                            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                                {projectDetail.brands.map((brand) => (
                                                    <li key={brand._id}>
                                                        <article className="overflow-hidden rounded border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                                                            <div className="flex items-center justify-center h-36 sm:h-40 bg-white p-3">
                                                                <img
                                                                    src={GetImageUrl(brand.brandLogo)}
                                                                    alt={`${brand.brandName} logo`}
                                                                    loading="lazy"
                                                                    className="max-h-full w-auto object-contain"
                                                                />
                                                            </div>

                                                            <div className="p-5">
                                                                <h4 className="line-clamp-2 text-sm sm:text-base leading-6 text-center text-gray-600">
                                                                    {brand.brandName}
                                                                </h4>
                                                            </div>
                                                        </article>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}

                                    {/* Products */}
                                    {projectDetail?.products?.length > 0 && (
                                        <section aria-labelledby="project-products">
                                            <h3
                                                id="project-products"
                                                className="mb-6 text-xl font-semibold text-gray-900"
                                            >
                                                Products Installed
                                            </h3>

                                            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                                {projectDetail.products.map((product) => (
                                                    <li
                                                        key={product._id}
                                                        className="cursor-pointer"
                                                         onClick={() =>
                                                             navigate(
                                                                 `${
                                                                     product.catalogType === "ip-network"
                                                                         ? "/it-products"
                                                                         : "/security-product"
                                                                 }/${product.slug}`,
                                                             )
                                                         }
                                                    >
                                                        <article className="overflow-hidden rounded border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                                                            <div className="flex items-center justify-center h-36 sm:h-40 bg-white p-3">
                                                                <img
                                                                    src={GetImageUrl(product.images?.[0]?.url)}
                                                                    alt={product.images?.[0]?.alt || product.title}
                                                                    loading="lazy"
                                                                    className="max-h-full w-auto object-contain"
                                                                />
                                                            </div>

                                                            <div className="p-5">
                                                                <h4 className="line-clamp-2 text-sm sm:text-base leading-6 text-center text-gray-600">
                                                                    {product.title}
                                                                </h4>
                                                            </div>
                                                        </article>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                </div>
                            </section>
                        )}

                    {projectDetail?.challenge && (
                        <ProjectContentSection
                            id="project-challenge"
                            title="Project Challenges"
                            subtitle="Every project presents unique challenges that require careful planning and execution."
                            content={projectDetail.challenge}
                        />
                    )}
                    {projectDetail?.solution && (
                        <ProjectContentSection
                            id="project-solution"
                            title="Our Security Solution"
                            subtitle="Discover how our team designed and implemented the right security solution."
                            content={projectDetail.solution}
                        />
                    )}
                    {projectDetail?.result && (
                        <ProjectContentSection
                            id="project-results"
                            title="Project Results"
                            subtitle="The completed project delivered measurable improvements in security, monitoring capabilities, and operational efficiency for the client."
                            content={projectDetail.result}
                        />
                    )}

                    {projectDetail?.gallery?.length > 0 && (
                        <section
                            aria-labelledby="project-gallery"
                            className="mt-12 md:mt-16 border-t border-gray-200 pt-10 md:pt-14"
                        >
                            <header className="mb-6 md:mb-8 max-w-4xl">
                                <h2
                                    id="project-gallery"
                                    className="
        text-2xl
        sm:text-3xl
        lg:text-4xl
        font-bold
        font-serif
        text-gray-900
        leading-tight
      "
                                >
                                    Project Gallery
                                </h2>

                                <p
                                    className="
        mt-3
        text-sm
        sm:text-base
        leading-7
        text-gray-600
      "
                                >
                                    Browse images captured during different stages of the project,
                                    showcasing installation quality, equipment, and the completed
                                    security system.
                                </p>
                            </header>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                                {projectDetail.gallery?.map((image, index) => (
                                    <figure
                                        key={index}
                                        className="
          group
          overflow-hidden
          rounded-md
          border
          border-gray-200
          bg-white
          shadow-sm
          transition-all
          duration-300
          hover:-translate-y-1
          hover:shadow-lg
        "
                                    >
                                        <div className="overflow-hidden">
                                            <img
                                                src={GetImageUrl(image.url)}
                                                alt={
                                                    image.caption ||
                                                    `${projectDetail.title} gallery image ${index + 1}`
                                                }
                                                loading="lazy"
                                                className="
              h-56
              sm:h-60
              lg:h-64
              w-full
              object-cover
              transition-transform
              duration-500
              group-hover:scale-105
            "
                                            />
                                        </div>

                                        {image.caption && (
                                            <figcaption
                                                className="
              p-4
              text-sm
              sm:text-base
              leading-6
              text-center
              text-gray-600
            "
                                            >
                                                {image.caption}
                                            </figcaption>
                                        )}
                                    </figure>
                                ))}
                            </div>
                        </section>
                    )}

                    {projectDetail?.videos?.length > 0 && (
                        <section
                            aria-labelledby="project-videos"
                            className="mt-12 md:mt-16 border-t border-gray-200 pt-10 md:pt-14"
                        >
                            <header className="mb-6 md:mb-8 max-w-4xl">
                                <h2
                                    id="project-videos"
                                    className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-gray-900 leading-tight"
                                >
                                    Project Videos
                                </h2>

                                <p className="mt-3 text-sm sm:text-base leading-7 text-gray-600">
                                    Watch installation highlights and demonstrations of the
                                    completed security system in operation.
                                </p>
                            </header>

                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                                {projectDetail.videos?.map((video, index) => {
                                    const videoId = getYoutubeId(video.url);

                                    if (!videoId) return null;

                                    return (
                                        <article
                                            key={index}
                                            className="
            group
            overflow-hidden
            rounded-md
            border
            border-gray-200
            bg-white
            shadow-sm
            transition-all
            duration-300
            hover:-translate-y-1
            hover:shadow-xl
          "
                                        >
                                            <div className="overflow-hidden bg-black">
                                                <div className="aspect-video">
                                                    <iframe
                                                        className="h-full w-full"
                                                        src={`https://www.youtube.com/embed/${videoId}`}
                                                        title={
                                                            video.caption || `Project Video ${index + 1}`
                                                        }
                                                        loading="lazy"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                    />
                                                </div>
                                            </div>

                                            {video.caption && (
                                                <h3
                                                    className="              p-4
              text-sm
              sm:text-base
              leading-6
              text-center
              text-gray-600"
                                                >
                                                    {video.caption}
                                                </h3>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {projectDetail?.faq?.length > 0 && (
                        <section
                            aria-labelledby="              frequently-asked-questions"
                            className="
      mt-12
      sm:mt-16
    "
                        >
                            <h2
                                className="
   text-2xl md:text-3xl
        font-bold
        leading-tight
        text-gray-900
        mb-5
        font-serif
      "
                            >
                                Frequently Asked Questions
                            </h2>
                            <div className="space-y-4 ">
                                {projectDetail?.faq?.map((item, index) => (
                                    <details
                                        key={item?._id || index}
                                        className="border border-gray-400 rounded p-4 sm:p-5 group"
                                    >
                                        <summary
                                            className="
        cursor-pointer
        list-none
        text-sm sm:text-base lg:text-lg
        text-gray-900
        flex
        justify-between
        items-center
      "
                                        >
                                            {item?.question}
                                            <span className="text-xl transition group-open:rotate-45">
                                                +
                                            </span>
                                        </summary>

                                        <p className="mt-3 text-sm sm:text-base leading-7 text-gray-600 text-justify">
                                            {item?.answer}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </section>
                    )}
                </article>
            </main>
        </>
    );
}
